import React, { useState, useEffect, useMemo, useRef } from "react";
import { Breadcrumb } from "../../components/Breadcrumb";
import { FORM_CONSTANTS } from "./constants";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { useLocation, useParams } from "react-router-dom";
import { FormData } from "./types";
import { useJsonTemplates, useBonusDetails } from "../../hooks/useBonusQueries";
import { getPreviousQuarter } from "../../utils/dateUtils";
import { ActionButtons } from "../../utils/renderActionButtons";
import { BonusApplicationComment, BonusApplicationHistoryItem } from "../../services/bonus/types";
import CustomerCareExpenseDCCMSubForm from "../../components/SubForms/DCCMSubForms/CustomerCareExpenseDCCMSubForm";
import CustomerCareCallsDCCMSubForm from "../../components/SubForms/DCCMSubForms/CustomerCareCallsDCCMSubForm";
import CommentComponent from "../../components/SubForms/Common/CommentComponent";
import BonusApplicationHistory from "../../components/SubForms/Common/BonusApplicationHistory";
import { getBonusSheetJsonData, parseBonusSheetJsonData } from "../../utils/bonusSheetParser";

const setByPath = <T,>(obj: T, path: string[], value: any): T => {
  const [head, ...rest] = path;
  if (!head) return obj;
  return { ...(obj as any), [head]: rest.length ? setByPath((obj as any)[head] ?? {}, rest, value) : value } as T;
};

const DCCMForm: React.FC = () => {
  const location = useLocation();
  const selectedRole = localStorage.getItem("selectedRole") || "";
  const { id } = useParams<{ id: string }>();
  const numericId = parseInt(id || "0");
  const hasValidId = numericId > 0;
  const { data: templates, isLoading } = useJsonTemplates(selectedRole);
  const { data: bonusDetails, isLoading: isLoadingDetails, refetch: refetchBonusDetails } = useBonusDetails(numericId);
  const totalCallsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [bonusSheetData, setBonusSheetData] = useState<any>(null);

  const queryParams = new URLSearchParams(location.search);
  const isReviewMode = queryParams.get("mode") === "review";
  const editableStatuses = [null, "Draft", "Rejected"];
  const isEditable = editableStatuses.includes(bonusDetails?.currentStatus ?? null);
  const exEmployeeQueryParam = queryParams.get("data") || null;
  const vpQueryParam = queryParams.get("vp") || null;
  const prevQuarterYear = getPreviousQuarter();
  const initQueryParam = queryParams.get("init") || null;

  const getFormTitle = (appType: string): string => {
    if (appType === "CCM3Manager" || appType === "CCM3MANAGER") return "CCM3-Manager Quarterly Bonus";
    return `${appType} Quarterly Bonus`;
  };

  const [loggedInUser, setLoggedInUser] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });

  const [bonusSheetTitle, setBonusSheetTitle] = useState<string>(getFormTitle(selectedRole));
  const [commentCounter, setCommentCounter] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [commentList, setCommentList] = useState<BonusApplicationComment[]>([]);
  const [commentsAccordionExpanded, setCommentsAccordionExpanded] = useState<boolean>(false);
  const [historyItems, setHistoryItems] = useState<BonusApplicationHistoryItem[]>([]);
  const [historyAccordionExpanded, setHistoryAccordionExpanded] = useState<boolean>(false);
  const [applicantInfo, setApplicantInfo] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });
  const [isFormHydrated, setIsFormHydrated] = useState<boolean>(!hasValidId);
  const hasRetriedHydrationRef = useRef(false);

  const [formData, setFormData] = useState<FormData>({
    managerName: ((templates?.hierarchy?.applicantName || localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "").toString(),
    managerEmail: ((templates?.hierarchy?.applicantEmail || localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "").toString(),

    applicationType: "",
    bonusRecipientName: "",
    bonusRecipientEmail: "",
    submitterName: (localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "",
    submitterEmail: (localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "",
    vpName: "",
    vpEmail: "",
    opsName: FORM_CONSTANTS.OPS.NAME,
    opsEmail: FORM_CONSTANTS.OPS.EMAIL,

    hasOverriddenHierarchy: false,
    jobTitle: "",
    jobFamily: "",

    communityNumber: "", year: prevQuarterYear.year.toString(), quarter: prevQuarterYear.quarter.toString(),
    operationalEfficiency: { customerCareExpense: { actualExpense: "", budget: "", percentOfBudget: "", bonusPercentEarned: "", bonusValue: "0.3", score: "" } },
    customerExperience: { customerCareCalls: { benchmark: "", under7Completed: "", totalCallsCompleted: "", percentCompletedUnder7: "", bonusPercentEarned: "", bonusValue: "0.7", score: "" } },
    bonusCalculation: { totalAchievementScore: { flatBonusRate: "0.00", bonusPoolAvailableAt100Percent: "" }, totalScore: "", percentage: "", bonusPool: "", totalBonusPayout: "" },
    signatures: { divisionManagerSignature: "", divisionManagerDate: "", vpSignature: "", vpDate: "" },
  });

  const initQueryData = useMemo(() => {
    if (!initQueryParam) return null;
    try {
      return JSON.parse(atob(decodeURIComponent(initQueryParam)));
    } catch (e) {
      console.error("Failed to parse ?init= param", e);
      return null;
    }
  }, [initQueryParam]);

  // ── All useEffects preserved exactly ─────────────────────────────────────
  useEffect(() => {
    setIsFormHydrated(!hasValidId);
    hasRetriedHydrationRef.current = false;
  }, [id]);

  useEffect(() => {
    if (hasValidId) {
      void refetchBonusDetails();
    }
  }, [id, refetchBonusDetails]);

  useEffect(() => {
    if (!initQueryData) return;
    if (bonusDetails || hasValidId) return;
    try {
      setBonusSheetTitle(getFormTitle(initQueryData.applicationType));
      setFormData(prev => ({
        ...prev,
        managerName: initQueryData.bonusRecipientName || prev.managerName,
        managerEmail: initQueryData.bonusRecipientEmail || prev.managerEmail,

        applicationType: initQueryData.applicationType || prev.applicationType,
        bonusRecipientName: initQueryData.bonusRecipientName || prev.bonusRecipientName,
        bonusRecipientEmail: initQueryData.bonusRecipientEmail || prev.bonusRecipientEmail,
        submitterName: initQueryData.submitterName || prev.submitterName,
        submitterEmail: initQueryData.submitterEmail || prev.submitterEmail,
        vpName: initQueryData.vpName || prev.vpName,
        vpEmail: initQueryData.vpEmail || prev.vpEmail,
        year: initQueryData.year?.toString() || prev.year, quarter: initQueryData.quarter?.toString() || prev.quarter,

        hasOverriddenHierarchy: initQueryData?.hasOverriddenHierarchy || false,
        jobTitle: initQueryData?.jobTitle || prev.jobTitle,
        jobFamily: initQueryData?.jobFamily || prev.jobFamily,

        signatures: { ...prev.signatures, vpSignature: initQueryData.vpName || prev.signatures.vpSignature }
      }));
      setApplicantInfo({ name: initQueryData.applicantName || "", email: initQueryData.applicantEmail || "" });
    } catch (e) { console.error("Failed to parse ?init= param", e); }
  }, [initQueryData]);

  useEffect(() => {
    if (!vpQueryParam) return;
    try {
      const vpData = JSON.parse(atob(decodeURIComponent(vpQueryParam)));
      if (vpData?.vpName) setFormData(prev => { if (prev.signatures?.vpSignature) return prev; return { ...prev, signatures: { ...prev.signatures, vpSignature: vpData.vpName } }; });
    } catch { }
  }, [vpQueryParam]);

  useEffect(() => {
    if (!bonusDetails && !id && !initQueryData && templates && templates?.jsonData && templates?.hierarchy) {
      try {
        const td = JSON.parse(templates.jsonData);
        if (templates.hierarchy?.applicantName && templates.hierarchy?.applicantEmail)
          setApplicantInfo({ name: templates.hierarchy.applicantName, email: templates.hierarchy.applicantEmail });
        setFormData(prev => ({
          ...prev,
          managerName: ((templates.hierarchy?.applicantName || localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "").toString(),
          managerEmail: ((templates.hierarchy?.applicantEmail || localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "").toString(),

          applicationType: td.applicationType || prev.applicationType,
          bonusRecipientName: td.bonusRecipientName || prev.bonusRecipientName,
          bonusRecipientEmail: td.bonusRecipientEmail || prev.bonusRecipientEmail,
          submitterName: td.submitterName || prev.submitterName,
          submitterEmail: td.submitterEmail || prev.submitterEmail,
          vpName: td.vpName || prev.vpName,
          vpEmail: td.vpEmail || prev.vpEmail,
          submitterRole: templates.hierarchy?.applicantRole || "",

          jobTitle: prev.jobTitle || td.jobTitle || "",
          jobFamily: prev.jobFamily || td.jobFamily || "",
          hasOverriddenHierarchy: td?.hasOverriddenHierarchy || false,


          year: td?.year || prev.year, quarter: td?.quarter || prev.quarter,
          operationalEfficiency: { customerCareExpense: { actualExpense: td?.operationalEfficiency?.customerCareExpense?.actualExpense || "", budget: td?.operationalEfficiency?.customerCareExpense?.budget || "", percentOfBudget: td?.operationalEfficiency?.customerCareExpense?.percentOfBudget || "", bonusPercentEarned: td?.operationalEfficiency?.customerCareExpense?.bonusPercentEarned || "", bonusValue: td?.operationalEfficiency?.customerCareExpense?.bonusValue || "0.3", score: td?.operationalEfficiency?.customerCareExpense?.score || "" } },
          customerExperience: { customerCareCalls: { benchmark: td?.customerExperience?.customerCareCalls?.benchmark || "", under7Completed: td?.customerExperience?.customerCareCalls?.under7Completed || "", totalCallsCompleted: td?.customerExperience?.customerCareCalls?.totalCallsCompleted || "", percentCompletedUnder7: td?.customerExperience?.customerCareCalls?.percentCompletedUnder7 || "", bonusPercentEarned: td?.customerExperience?.customerCareCalls?.bonusPercentEarned || "", bonusValue: td?.customerExperience?.customerCareCalls?.bonusValue || "0.7", score: td?.customerExperience?.customerCareCalls?.score || "" } },
          bonusCalculation: { totalAchievementScore: { flatBonusRate: td?.bonusCalculation?.totalAchievementScore?.flatBonusRate || "0.00", bonusPoolAvailableAt100Percent: td?.bonusCalculation?.totalAchievementScore?.bonusPoolAvailableAt100Percent || "" }, totalScore: td?.bonusCalculation?.totalScore || "", percentage: td?.bonusCalculation?.percentage || "", bonusPool: td?.bonusCalculation?.bonusPool || "", totalBonusPayout: td?.bonusCalculation?.totalBonusPayout || "" },
          signatures: td?.signatures || { divisionManagerSignature: "", divisionManagerDate: "", vpSignature: "", vpDate: "" },
        }));
      } catch (error) { console.error("Error parsing template data:", error); }
    }
  }, [templates]);

  useEffect(() => {
    if (bonusDetails && id) {
      try {
        const rawBonusSheet = getBonusSheetJsonData(bonusDetails);
        if (!rawBonusSheet) {
          setIsFormHydrated(false);
          return;
        }
        const bsd = parseBonusSheetJsonData(rawBonusSheet) as any;
        setBonusSheetData(bsd);

        const normalizedStatus = (bonusDetails?.currentStatus || "")
          .toString()
          .trim()
          .replace(/ /g, "_")
          .toLowerCase();
        const isDraftLike = ["draft", "rejected", ""].includes(normalizedStatus);
        const hasSavedPayload =
          !!bsd &&
          typeof bsd === "object" &&
          ("operationalEfficiency" in bsd ||
            "customerExperience" in bsd ||
            "bonusCalculation" in bsd);
        if (!isDraftLike && !hasSavedPayload) {
          setIsFormHydrated(false);
          if (!hasRetriedHydrationRef.current) {
            hasRetriedHydrationRef.current = true;
            void refetchBonusDetails();
          }
          return;
        }
        hasRetriedHydrationRef.current = false;
        setBonusSheetTitle(getFormTitle(bonusDetails.applicationType));
        setCommentList(bonusDetails.comments || []);
        setHistoryItems(bonusDetails.historyItems || []);

        setFormData(prev => {
          const recipientNameLoaded = bsd?.bonusRecipientName || bsd?.managerName || bsd?.applicantName || bonusDetails?.employeeName || prev.bonusRecipientName;
          const recipientEmailLoaded = bsd?.bonusRecipientEmail || bsd?.managerEmail || bsd?.applicantEmail || bonusDetails?.bonusRecipientEmail || prev.bonusRecipientEmail;
          return {
            ...prev,
            managerName: recipientNameLoaded,
            managerEmail: recipientEmailLoaded,
            applicationType: bonusDetails?.applicationType || prev.applicationType,
            bonusRecipientName: recipientNameLoaded,
            bonusRecipientEmail: recipientEmailLoaded,
            submitterName: bonusDetails?.submittedByName || prev.submitterName,
            submitterEmail: bonusDetails?.submittedBy || prev.submitterEmail,
            vpName: bsd?.vpName || prev.vpName,
            vpEmail: bsd?.vpEmail || prev.vpEmail,
            opsName: bsd?.opsName || prev.opsName,
            opsEmail: bsd?.opsEmail || prev.opsEmail,

            jobTitle: bsd?.jobTitle || prev.jobTitle,
            jobFamily: bsd?.jobFamily || prev.jobFamily,
            hasOverriddenHierarchy: bonusDetails?.hasOverriddenHierarchy || false,

            year: bonusDetails.year.toString() || prev.year,
            quarter: bonusDetails.quarter.toString() || prev.quarter,
            operationalEfficiency: bsd?.operationalEfficiency || prev.operationalEfficiency,
            customerExperience: bsd?.customerExperience || prev.customerExperience,
            bonusCalculation: bsd?.bonusCalculation || prev.bonusCalculation,
            signatures: bsd?.signatures || prev.signatures
          };
        });

        if (bonusDetails.createdBy) setApplicantInfo(prev => ({ ...prev, email: bonusDetails.createdBy }));
        setIsFormHydrated(true);
      } catch (error) { console.error("Error parsing bonus sheet data:", error); }
    }
  }, [bonusDetails, id, refetchBonusDetails]);

  const getBonusPercentEarned = (percentCompleted: number): number => {
    const thresholds = [{ min: 80, bonus: 120 }, { min: 75, bonus: 110 }, { min: 70, bonus: 100 }, { min: 66, bonus: 95 }, { min: 63, bonus: 90 }, { min: 56, bonus: 80 }, { min: 50, bonus: 50 }, { min: 0, bonus: 0 }];
    return thresholds.find(t => percentCompleted >= t.min)?.bonus ?? 0;
  };

  const derivedCalculations = useMemo(() => {
    const expA = parseFloat(formData.operationalEfficiency.customerCareExpense.actualExpense || "0");
    const expB = parseFloat(formData.operationalEfficiency.customerCareExpense.budget || "0");
    const expPct = expB ? expA / expB : 0;
    const expInv = expPct !== 0 ? 1 / expPct : 0;
    const expBPE = Math.max(0, Math.min(expInv, 1.2)) * Number(expInv >= 0.7) * 100;
    const expBV = parseFloat(formData.operationalEfficiency.customerCareExpense.bonusValue || "0.3");
    const expScore = expBPE * expBV;
    const u7 = parseFloat(formData.customerExperience.customerCareCalls.under7Completed || "0");
    const tot = parseFloat(formData.customerExperience.customerCareCalls.totalCallsCompleted || "0");
    const pctU7 = tot ? Math.round((u7 / tot) * 100) : 0;
    const callBPE = getBonusPercentEarned(pctU7);
    const callBV = parseFloat(formData.customerExperience.customerCareCalls.bonusValue) || 0.7;
    const callScore = callBPE * callBV;
    const flatRate = parseFloat(formData.bonusCalculation.totalAchievementScore.flatBonusRate || "0");
    const totalScore = expScore + callScore;
    const pct = totalScore * 0.01;
    const bonusPool = flatRate;
    const totalPayout = bonusPool * pct;
    return {
      actualExpense: expA.toFixed(2), budget: expB.toFixed(2), percentOfBudget: expPct.toFixed(2),
      expenseBonusPercentEarned: expBPE.toFixed(2), expenseBonusValue: expBV.toFixed(2), expenseScore: expScore.toFixed(2),
      percentCompletedUnder7: pctU7.toFixed(2), callsBonusPercentEarned: callBPE.toFixed(2), callsBonusValue: callBV.toFixed(2), callsScore: callScore.toFixed(2),
      flatBonusRate: flatRate.toFixed(2), bonusPoolAvailableAt100Percent: bonusPool.toFixed(2),
      totalScore: totalScore.toFixed(2), percentage: pct.toFixed(2), bonusPool: bonusPool.toFixed(2), totalBonusPayout: totalPayout.toFixed(2),
    };
  }, [formData.operationalEfficiency.customerCareExpense.actualExpense, formData.operationalEfficiency.customerCareExpense.budget, formData.operationalEfficiency.customerCareExpense.bonusValue, formData.customerExperience.customerCareCalls.under7Completed, formData.customerExperience.customerCareCalls.totalCallsCompleted, formData.customerExperience.customerCareCalls.bonusValue, formData.bonusCalculation.totalAchievementScore.flatBonusRate]);

  useEffect(() => {
    setFormData(prev => {
      if (prev.operationalEfficiency.customerCareExpense.actualExpense === derivedCalculations.actualExpense && prev.operationalEfficiency.customerCareExpense.budget === derivedCalculations.budget && prev.operationalEfficiency.customerCareExpense.percentOfBudget === derivedCalculations.percentOfBudget && prev.operationalEfficiency.customerCareExpense.bonusPercentEarned === derivedCalculations.expenseBonusPercentEarned && prev.operationalEfficiency.customerCareExpense.score === derivedCalculations.expenseScore && prev.customerExperience.customerCareCalls.percentCompletedUnder7 === derivedCalculations.percentCompletedUnder7 && prev.customerExperience.customerCareCalls.bonusPercentEarned === derivedCalculations.callsBonusPercentEarned && prev.customerExperience.customerCareCalls.score === derivedCalculations.callsScore && prev.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent === derivedCalculations.bonusPoolAvailableAt100Percent && prev.bonusCalculation.totalScore === derivedCalculations.totalScore && prev.bonusCalculation.percentage === derivedCalculations.percentage && prev.bonusCalculation.bonusPool === derivedCalculations.bonusPool && prev.bonusCalculation.totalBonusPayout === derivedCalculations.totalBonusPayout) return prev;
      return { ...prev, operationalEfficiency: { ...prev.operationalEfficiency, customerCareExpense: { ...prev.operationalEfficiency.customerCareExpense, actualExpense: derivedCalculations.actualExpense, budget: derivedCalculations.budget, percentOfBudget: derivedCalculations.percentOfBudget, bonusPercentEarned: derivedCalculations.expenseBonusPercentEarned, score: derivedCalculations.expenseScore } }, customerExperience: { ...prev.customerExperience, customerCareCalls: { ...prev.customerExperience.customerCareCalls, percentCompletedUnder7: derivedCalculations.percentCompletedUnder7, bonusPercentEarned: derivedCalculations.callsBonusPercentEarned, score: derivedCalculations.callsScore } }, bonusCalculation: { ...prev.bonusCalculation, totalAchievementScore: { ...prev.bonusCalculation.totalAchievementScore, flatBonusRate: derivedCalculations.flatBonusRate, bonusPoolAvailableAt100Percent: derivedCalculations.bonusPoolAvailableAt100Percent }, totalScore: derivedCalculations.totalScore, percentage: derivedCalculations.percentage, bonusPool: derivedCalculations.bonusPool, totalBonusPayout: derivedCalculations.totalBonusPayout } };
    });
  }, [derivedCalculations]);

  useEffect(() => { return () => { if (totalCallsDebounceRef.current) clearTimeout(totalCallsDebounceRef.current); }; }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, options?: { type?: 'number' | 'decimal' | 'text'; maxLength?: number; minLength?: number; max?: number; min?: number }) => {
    let { name, value } = e.target;
    if (options) { if (options.maxLength !== undefined && value.length > options.maxLength) return; if (options.minLength !== undefined && value !== '' && value.length < options.minLength) return; }
    value = typeof value === "string" ? value.replace(/,/g, "").trim() : "";
    const type = options?.type || "text";
    if (type === "number") { if (!(value === '' || /^[0-9]*$/.test(value))) return; }
    else if (type === "decimal") { if (!(value === '' || /^\d*\.?\d{0,2}$/.test(value))) return; }
    if (name === "customerExperience.customerCareCalls.under7Completed") { const tc = parseInt(formData.customerExperience.customerCareCalls.totalCallsCompleted || "0"); const u7v = parseInt(value || "0"); if (formData.customerExperience.customerCareCalls.totalCallsCompleted && tc > 0 && u7v > tc) value = tc.toString(); }
    if (name === "customerExperience.customerCareCalls.totalCallsCompleted") {
      if (totalCallsDebounceRef.current) clearTimeout(totalCallsDebounceRef.current);
      setFormData(prev => setByPath(prev, name.split("."), value));
      const u7 = parseInt(formData.customerExperience.customerCareCalls.under7Completed || "0");
      const tcv = parseInt(value || "0");
      if (formData.customerExperience.customerCareCalls.under7Completed && u7 > 0 && value !== '' && tcv < u7)
        totalCallsDebounceRef.current = setTimeout(() => setFormData(prev => setByPath(prev, ["customerExperience", "customerCareCalls", "under7Completed"], value)), 500);
      return;
    }
    setFormData(prev => setByPath(prev, name.split("."), value));
  };

  const validateForm = () => {
    const errors: string[] = [];
    if (isReviewMode && (bonusDetails?.currentStatus === undefined || bonusDetails?.currentStatus === "Draft" || bonusDetails?.currentStatus === "Rejected")) {
      if (!formData.operationalEfficiency.customerCareExpense.actualExpense || parseFloat(formData.operationalEfficiency.customerCareExpense.actualExpense) <= 0) errors.push("Customer Care Expense: Actual Expense is required.");
      if (!formData.operationalEfficiency.customerCareExpense.budget || parseFloat(formData.operationalEfficiency.customerCareExpense.budget) <= 0) errors.push("Customer Care Expense: Budget is required.");
      if (!formData.customerExperience.customerCareCalls.under7Completed || parseInt(formData.customerExperience.customerCareCalls.under7Completed) < 0) errors.push("Customer Care Calls: Under 7 Completed is required.");
      if (!formData.customerExperience.customerCareCalls.totalCallsCompleted || parseInt(formData.customerExperience.customerCareCalls.totalCallsCompleted) <= 0) errors.push("Customer Care Calls: Total Calls Completed is required.");
      if (!formData.bonusCalculation.totalAchievementScore.flatBonusRate || parseFloat(formData.bonusCalculation.totalAchievementScore.flatBonusRate) <= 0) errors.push("Bonus Calculation: Flat Bonus Rate is required.");
    }
    return errors;
  };

  const hasOverriddenHierarchy = initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy;
  const recipientEmail = initQueryData?.bonusRecipientDetails?.Email || bonusDetails?.bonusRecipientEmail;
  const recipientName = initQueryData?.bonusRecipientDetails?.EmployeeName || bonusDetails?.employeeName;
  const recipientJobTitle = initQueryData?.bonusRecipientDetails?.JobTitle || bonusSheetData?.jobTitle || formData.jobTitle || "";
  const recipientJobFamily = initQueryData?.bonusRecipientDetails?.JobFamily || bonusSheetData?.jobFamily || formData.jobFamily || "";
  const applicationType = initQueryData?.applicationType || bonusDetails?.applicationType;

  const buildMetadata = () => {
    const recipientNameFinal = recipientName || formData.bonusRecipientName || bonusDetails?.employeeName || "";
    const recipientEmailFinal = recipientEmail || formData.bonusRecipientEmail || "";
    return {
      applicationType: applicationType || formData.applicationType || "",
      bonusRecipientName: recipientNameFinal,
      bonusRecipientEmail: recipientEmailFinal,
      managerName: recipientNameFinal,
      managerEmail: recipientEmailFinal,
      applicantName: recipientNameFinal,
      applicantEmail: recipientEmailFinal,
      submitterName: initQueryData?.submitterName || bonusDetails?.submittedByName || formData.submitterName || "",
      submitterEmail: initQueryData?.submitterEmail || bonusDetails?.submittedBy || formData.submitterEmail || "",
      vpName: formData.vpName || "",
      vpEmail: formData.vpEmail || "",
      opsName: formData.opsName || "",
      opsEmail: formData.opsEmail || "",
      hasOverriddenHierarchy: formData.hasOverriddenHierarchy || false,
      jobTitle: recipientJobTitle || formData?.jobTitle || "",
      jobFamily: recipientJobFamily || formData?.jobFamily || "",
    };
  };

  const getBonusSheetJson = () => btoa(JSON.stringify({
    operationalEfficiency: { customerCareExpense: { actualExpense: formData.operationalEfficiency.customerCareExpense.actualExpense, budget: formData.operationalEfficiency.customerCareExpense.budget, percentOfBudget: formData.operationalEfficiency.customerCareExpense.percentOfBudget, bonusPercentEarned: formData.operationalEfficiency.customerCareExpense.bonusPercentEarned, bonusValue: formData.operationalEfficiency.customerCareExpense.bonusValue, score: formData.operationalEfficiency.customerCareExpense.score } },
    customerExperience: { customerCareCalls: { benchmark: formData.customerExperience.customerCareCalls.benchmark, under7Completed: formData.customerExperience.customerCareCalls.under7Completed, totalCallsCompleted: formData.customerExperience.customerCareCalls.totalCallsCompleted, percentCompletedUnder7: formData.customerExperience.customerCareCalls.percentCompletedUnder7, bonusPercentEarned: formData.customerExperience.customerCareCalls.bonusPercentEarned, bonusValue: formData.customerExperience.customerCareCalls.bonusValue, score: formData.customerExperience.customerCareCalls.score } },
    bonusCalculation: { totalAchievementScore: { flatBonusRate: formData.bonusCalculation.totalAchievementScore.flatBonusRate, bonusPoolAvailableAt100Percent: formData.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent }, totalScore: formData.bonusCalculation.totalScore, percentage: formData.bonusCalculation.percentage, bonusPool: formData.bonusCalculation.bonusPool, totalBonusPayout: formData.bonusCalculation.totalBonusPayout },
    signatures: formData.signatures,
    ...buildMetadata(),
  }));

  const handleHistoryAccordionToggle = () => setHistoryAccordionExpanded(p => !p);
  const handleCommentsAccordionToggle = () => setCommentsAccordionExpanded(p => !p);
  const handleCommentChange = (value: string) => setComment(value);
  const handleAddComment = (currentComment: string) => {
    setComment(currentComment);
    if (comment.trim() !== "") {
      const centralDate = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }));
      const newComment: BonusApplicationComment = { commentId: commentCounter, bonusApplicationId: bonusDetails?.bonusApplicationId || 0, commentText: comment, commentedBy: localStorage.getItem("loggedInUserFullName") || "", commentedOn: centralDate.toISOString(), commentedByEmail: localStorage.getItem("loggedInUserEmail") || "", statusAtTimeOfComment: bonusDetails?.currentStatus.replaceAll("_", " ") ?? "Draft", statusIdAtTimeOfComment: 0, roleTypeName: selectedRole, roleTypeId: 0, isActive: true, isSystemGenerated: false };
      setCommentList(prev => [newComment, ...prev]);
      setComment("");
      setCommentCounter(prev => prev - 1);
    }
  };

  const breadcrumbPages = [{ name: FORM_CONSTANTS.BREADCRUMB.TITLE, href: FORM_CONSTANTS.BREADCRUMB.PATH, current: true, icon: (props: React.ComponentProps<"svg">) => <ClipboardDocumentCheckIcon {...props} className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900" /> }];

  if (isLoading || isLoadingDetails || (hasValidId && !isFormHydrated)) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-page)" }}>
      <div style={{ color: "var(--text-muted)" }}>Loading…</div>
    </div>
  );

  const status = bonusDetails?.currentStatus?.replaceAll("_", " ") ?? "Draft";
  const statusColors: Record<string, React.CSSProperties> = {
    Draft: { background: "#FEF9C3", color: "#854D0E", border: "1px solid #FDE047" },
    Submitted: { background: "#DBEAFE", color: "#1E40AF", border: "1px solid #93C5FD" },
    Approved: { background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" },
    Rejected: { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" },
    Paid: { background: "#D1FAE5", color: "#065F46", border: "1px solid #6EE7B7" },
  };
  const statusStyle = statusColors[status] ?? statusColors["Draft"];

  const fmtNum = (v: string) => v && v !== "" && !isNaN(Number(v)) ? parseFloat(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : "";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-page)" }}>
      <div className="flex flex-col">
        <main className="flex flex-col pb-8">
          <div className={`mx-auto w-full pt-6 ${FORM_CONSTANTS.LAYOUT.MAX_WIDTH} ${FORM_CONSTANTS.LAYOUT.PADDING}`}>
            <Breadcrumb pages={breadcrumbPages} className="mb-6 ml-2" />

            <div style={{ background: "var(--bg-surface)", borderRadius: 12, border: "1px solid var(--border-default)", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>

              {/* ── Card header ─────────────────────────────────────────── */}
              <div style={{ borderBottom: "1px solid var(--border-section)", padding: "20px 24px", background: "var(--bg-form-header)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>{bonusSheetTitle}</h1>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, ...statusStyle }}>{status}</span>
                </div>
                <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", lineHeight: 1.5 }}>
                  Achievement of Targets is a subjective determination in the Employer's sole discretion. The Employer may change the percentages or Targets at any time.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "end" }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Bonus Recipient</label>
                    <input type="text" name="managerName" value={formData.managerName} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, fontWeight: 500, color: "var(--text-primary)", background: "var(--bg-input-readonly)", border: "1px solid var(--border-readonly)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "end" }}>
                    {[{ label: "Year", name: "year", value: formData.year }, { label: "Quarter", name: "quarter", value: formData.quarter }].map(f => (
                      <div key={f.name}>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{f.label}</label>
                        <input type="text" name={f.name} value={f.value} readOnly
                          style={{ width: "8ch", padding: "7px 10px", fontSize: 13, color: "var(--text-readonly)", background: "var(--bg-input-readonly)", border: "1px solid var(--border-readonly)", borderRadius: 7, outline: "none", cursor: "not-allowed" }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Override active banner */}
              {hasOverriddenHierarchy && (
                <div style={{ background: "#fff5f5", border: "1.5px solid #f87171", borderRadius: "8px", padding: "12px 16px", fontSize: 12, color: "#991b1b", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Override Active: Employee Hierarchy is Overridden</span>
                  </div>
                  <span style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
                    {status === "Draft"
                      ? "This recipient was selected with an active hierarchy override — all Construction & Customer Care employees were available for selection, not just direct reports."
                      : "This recipient was selected under a hierarchy override — the standard reporting hierarchy was not enforced at the time of selection."}
                  </span>
                  {(recipientName || recipientJobTitle || recipientJobFamily || applicationType) && (
                    <div style={{ marginTop: 4, padding: "8px 10px", background: "#fee2e2", borderRadius: "6px", display: "flex", flexWrap: "wrap", gap: "4px 20px", color: "#7f1d1d" }}>
                      {recipientName && <span><span style={{ fontWeight: 600 }}>Recipient:</span> {recipientName}</span>}
                      {recipientJobTitle && <span><span style={{ fontWeight: 600 }}>Recipient Job Title:</span> {recipientJobTitle}</span>}
                      {recipientJobFamily && <span><span style={{ fontWeight: 600 }}>Job Family:</span> {recipientJobFamily}</span>}
                      {applicationType && <span><span style={{ fontWeight: 600 }}>Application Type:</span> {applicationType}</span>}
                    </div>
                  )}
                </div>
              )}

              {/* ── OPERATIONAL EFFICIENCY ─────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Operational Efficiency</span>
                </div>
                <CustomerCareExpenseDCCMSubForm data={formData.operationalEfficiency.customerCareExpense} isEditable={isEditable} onInputChange={handleInputChange} />
              </div>

              {/* ── CUSTOMER EXPERIENCE ────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Customer Experience</span>
                </div>
                <CustomerCareCallsDCCMSubForm data={formData.customerExperience.customerCareCalls} isEditable={isEditable} payoutTiers={FORM_CONSTANTS.PAYOUT_TIERS.CUSTOMER_CARE_CALLS} onInputChange={handleInputChange} />
              </div>

              {/* ── BONUS CALCULATION — inline (no sub-form component) ─── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                {/* Section header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Bonus Calculation</span>
                </div>

                {/* Total Achievement Score label */}
                <div style={{ padding: "8px 16px", background: "var(--bg-surface-raised)", borderBottom: "1px solid var(--border-section)", textAlign: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Total Achievement Score</span>
                </div>

                {/* Flat Bonus Rate / Bonus Pool row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid var(--border-section)", background: "var(--bg-surface-raised)" }}>
                  <div style={{ padding: "10px 16px", borderRight: "1px solid var(--border-section)" }}>
                    <div className="sf-calc-label">Flat Bonus Rate</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>$</span>
                      <input type="text" name="bonusCalculation.totalAchievementScore.flatBonusRate"
                        value={fmtNum(formData.bonusCalculation.totalAchievementScore.flatBonusRate) || ""}
                        inputMode="decimal" pattern="^\d*\.?\d{0,2}$" maxLength={12}
                        onChange={e => handleInputChange(e, { type: "decimal", maxLength: 12 })}
                        disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
                        className="sf-input" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
                    </div>
                  </div>
                  <div style={{ padding: "10px 16px" }}>
                    <div className="sf-calc-label">Bonus Pool Available at 100%</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>$</span>
                      <input type="text" readOnly name="bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100"
                        value={fmtNum(formData.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent) || ""}
                        className="sf-input-ro" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
                    </div>
                  </div>
                </div>

                {/* Total Score / % / Bonus Pool / Payout */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: "var(--bg-surface-raised)" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderRight: "1px solid var(--border-section)" }}>
                    {[
                      { label: "Total Score", name: "bonusCalculation.totalScore", value: formData.bonusCalculation.totalScore, prefix: null },
                      { label: "%", name: "bonusCalculation.percentage", value: formData.bonusCalculation.percentage, prefix: null },
                      { label: "Bonus Pool", name: "bonusCalculation.bonusPool", value: fmtNum(formData.bonusCalculation.bonusPool), prefix: "$" },
                    ].map((col, i) => (
                      <div key={i} style={{ padding: "10px 12px", textAlign: "center", borderLeft: i > 0 ? "1px solid var(--border-section)" : "none" }}>
                        <div className="sf-calc-label">{col.label}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          {col.prefix && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{col.prefix}</span>}
                          <input type="text" readOnly name={col.name} value={col.value}
                            className="sf-input-ro" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: "12px 16px", background: "var(--bg-chip)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Total Bonus Payout</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 15, color: "var(--text-muted)" }}>$</span>
                      <input type="text" readOnly name="bonusCalculation.totalBonusPayout"
                        value={fmtNum(formData.bonusCalculation.totalBonusPayout) || "0"}
                        style={{ background: "transparent", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", width: 160 }} />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </main>
      </div>

      <BonusApplicationHistory historyItems={historyItems} comments={commentList} historyAccordionExpanded={historyAccordionExpanded} onAccordionToggle={handleHistoryAccordionToggle} />
      <CommentComponent comment={comment} commentList={commentList} commentsAccordionExpanded={commentsAccordionExpanded} onCommentChange={handleCommentChange} onAddComment={handleAddComment} onAccordionToggle={handleCommentsAccordionToggle} />

      <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-section)", background: "var(--bg-surface-raised)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <ActionButtons
          isReviewMode={isReviewMode}
          bonusApplicationId={id && parseInt(id) ? parseInt(id) : bonusDetails?.bonusApplicationId}
          currentStatus={bonusDetails?.currentStatus ?? null}
          bonusSheetJson={getBonusSheetJson()}
          projectNumbers={null}
          amount={parseFloat(formData.bonusCalculation.totalBonusPayout) || 0}
          bonusRecipientEmail={formData.bonusRecipientEmail}
          submitterEmail={formData.submitterEmail}
          submitterName={formData.submitterName}
          vpEmail={formData.vpEmail}
          applicationType={formData.applicationType}
          hasOverriddenHierarchy={initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy || false}
          comments={commentList.filter((c: BonusApplicationComment) => c.commentId <= 0).map((c: BonusApplicationComment) => c.commentText)}
          formValidationErrors={validateForm()}
          recipientName={formData.managerName}
          recipientJobFamily={recipientJobFamily}
          recipientJobTitle={recipientJobTitle}
          loggedInUserName={loggedInUser.name}

        />
      </div>
    </div>
  );
};

export default DCCMForm;